/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import swal from "sweetalert";

// @mui
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PlaceIcon from "@mui/icons-material/Place";
import PersonIcon from "@mui/icons-material/Person";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import LinkIcon from "@mui/icons-material/Link";

import { QRCodeSVG } from "qrcode.react";

import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import { API_ESA_OFFICE } from "utils";

function getStatusEventLabel(status) {
  switch (status) {
    case "satu_kali": return "Satu Kali";
    case "rutin": return "Rutin";
    case "evaluasi": return "Evaluasi";
    case "asistensi": return "Asistensi";
    default: return status;
  }
}

function getStatusEventColor(status) {
  switch (status) {
    case "satu_kali": return "info";
    case "rutin": return "warning";
    case "evaluasi": return "success";
    case "asistensi": return "secondary";
    default: return "default";
  }
}

function getScopeLabel(scope) {
  if (!scope) return "-";
  const s = String(scope).toLowerCase();
  if (s === "internal") return "Internal";
  if (s === "pemda") return "Pemda";
  if (s === "publik") return "Publik";
  return scope;
}

function getScopeColor(scope) {
  if (!scope) return "default";
  const s = String(scope).toLowerCase();
  if (s === "internal") return "primary";
  if (s === "pemda") return "info";
  if (s === "publik") return "success";
  return "default";
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d)) return "-";
  return d.toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
}

function EventSaya() {
  const [detailEventsList, setDetailEventsList] = useState([]);
  const [dinasId, setDinasId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [searchQuery, setSearchQuery] = useState("");
  const [openQrDialog, setOpenQrDialog] = useState(false);
  const [qrDialogEvent, setQrDialogEvent] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
      setUserId((user.user_id ?? user.uid) ? String(user.user_id ?? user.uid) : null);
    } else {
      logoutUser(navigate);
    }
  }, [navigate]);

  const fetchDetailEvents = async () => {
    if (!dinasId || !userId) return;

    setLoading(true);
    try {
      let url = `${API_ESA_OFFICE}events/detail/all?dinasId=${encodeURIComponent(dinasId)}&userId=${encodeURIComponent(userId)}`;
      if (tahun) {
        url += `&tahun=${tahun}`;
      }
      const response = await axios.get(url);
      const data = response.data || [];
      // Pastikan id_pembuat selalu string untuk konsistensi
      // Pastikan id_events ada di setiap item
      const formattedData = data.map((item) => ({
        ...item,
        id_pembuat: item.id_pembuat ? String(item.id_pembuat) : null,
        id_events: item.id_events || null,
        dinas_peserta_nama: item.dinas_peserta_nama || null,
        event_nama_pembuat: item.event_nama_pembuat || null,
        event_uid_RSS_pembuat: item.event_uid_RSS_pembuat || null,
        event_id_pembuat: item.event_id_pembuat || null,
      }));
      console.log(
        "fetchDetailEvents - formattedData sample:",
        formattedData.length > 0 ? formattedData[0] : "No data"
      );
      setDetailEventsList(formattedData);
    } catch (error) {
      console.error("Error fetching detail events:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data detail events",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dinasId && userId) {
      fetchDetailEvents();
    }
  }, [dinasId, userId, tahun]);

  // Kelompokkan detail events berdasarkan event
  const groupedEvents = useMemo(() => {
    const grouped = {};
    detailEventsList.forEach((detailEvent) => {
      const eventId = detailEvent.id_events;
      if (!grouped[eventId]) {
        grouped[eventId] = {
          id_events: eventId,
          kegiatan: detailEvent.kegiatan || "-",
          status_event: detailEvent.status_event || "-",
          scope: detailEvent.scope || null,
          nama_dinas: detailEvent.nama_dinas,
          nama_bidang: detailEvent.nama_bidang,
          uid_RSS_pembuat: detailEvent.event_uid_RSS_pembuat || null,
          nama_pembuat: detailEvent.event_nama_pembuat || null,
          event_id_pembuat: detailEvent.event_id_pembuat || null,
          detailEvents: [],
        };
      }
      grouped[eventId].detailEvents.push(detailEvent);
    });

    const groups = Object.values(grouped).map((group) => {
      const sorted = [...group.detailEvents].sort((a, b) => {
        const ta = a.waktu_mulai ? new Date(a.waktu_mulai).getTime() : 0;
        const tb = b.waktu_mulai ? new Date(b.waktu_mulai).getTime() : 0;
        return tb - ta;
      });
      const latestDetail = sorted[0];
      const latestWaktuMulai = latestDetail?.waktu_mulai
        ? new Date(latestDetail.waktu_mulai).getTime()
        : 0;
      const totalPeserta = group.detailEvents.reduce(
        (sum, de) => sum + (parseInt(de.jumlah_peserta, 10) || 0),
        0
      );
      const dinasPesertaSet = new Set();
      group.detailEvents.forEach((de) => {
        const nama = de.dinas_peserta_nama?.trim();
        if (nama) dinasPesertaSet.add(nama);
      });
      const dinasPesertaList = Array.from(dinasPesertaSet);
      return {
        ...group,
        jumlah_detail_events: group.detailEvents.length,
        latestWaktuMulai,
        latestWaktuFormatted: formatDate(latestDetail?.waktu_mulai),
        latestTempat: latestDetail?.tempat || "-",
        totalPeserta,
        dinasPesertaList,
      };
    });
    return groups.sort((a, b) => b.latestWaktuMulai - a.latestWaktuMulai);
  }, [detailEventsList]);

  const filteredGroupedEvents = useMemo(() => {
    if (!searchQuery.trim()) return groupedEvents;
    const query = searchQuery.toLowerCase().trim();
    return groupedEvents.filter((g) => g.kegiatan?.toLowerCase().includes(query));
  }, [groupedEvents, searchQuery]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Event Saya
                </MDTypography>
              </MDBox>
              <MDBox pt={4} pb={3} px={3}>
                {/* Filter Tahun */}
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      options={(() => {
                        const currentYear = new Date().getFullYear();
                        const years = [];
                        for (let i = 2025; i <= currentYear; i++) {
                          years.push({ label: i.toString(), value: i });
                        }
                        return years.reverse();
                      })()}
                      value={
                        tahun ? { label: tahun.toString(), value: tahun } : null
                      }
                      onChange={(event, newValue) => {
                        if (newValue) setTahun(newValue.value);
                      }}
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Tahun"
                          variant="standard"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <MDInput
                      label="Cari Nama Event"
                      variant="standard"
                      fullWidth
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
                {loading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                  >
                    <CircularProgress />
                    <MDTypography variant="body2" color="text" ml={2}>
                      Memuat data...
                    </MDTypography>
                  </MDBox>
                ) : filteredGroupedEvents.length === 0 ? (
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        py={4}
                      >
                        <MDTypography variant="h6" color="text.secondary">
                          {searchQuery.trim()
                            ? "Tidak ada event yang sesuai dengan pencarian"
                            : "Tidak ada data event"}
                        </MDTypography>
                        <MDTypography variant="body2" color="text.secondary">
                          {searchQuery.trim()
                            ? "Coba ubah kata kunci pencarian"
                            : "Belum ada event yang Anda buat"}
                        </MDTypography>
                      </MDBox>
                    ) : (
                      <Grid container spacing={2}>
                        {filteredGroupedEvents.map((group) => (
                          <Grid item xs={12} sm={6} md={4} key={group.id_events}>
                            <Card
                                sx={{
                                p: 2,
                                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                                transition: "box-shadow 0.2s, transform 0.2s",
                              "&:hover": {
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                                  transform: "translateY(-1px)",
                              },
                            }}
                          >
                              <MDBox display="flex" flexDirection="column" gap={1.5}>
                                <MDTypography variant="body2" sx={{ lineHeight: 1.35, fontSize: "0.875rem", fontWeight: 400 }}>
                                  {group.kegiatan}
                                </MDTypography>
                                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <Chip
                                    label={getStatusEventLabel(group.status_event)}
                                    color={getStatusEventColor(group.status_event)}
                                  size="small"
                                  variant="filled"
                                />
                                  {group.scope && (
                                    <Chip
                                      label={getScopeLabel(group.scope)}
                                      color={getScopeColor(group.scope)}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                  <MDTypography variant="caption" color="text.secondary">
                                    {group.jumlah_detail_events} sesi
                                </MDTypography>
                              </MDBox>
                                <MDBox display="flex" flexDirection="column" gap={0.5} sx={{ "& .MuiSvgIcon-root": { fontSize: 14, mr: 0.5, verticalAlign: "middle" } }}>
                                  <MDBox display="flex" alignItems="center" gap={0.5}>
                                    <CalendarTodayIcon sx={{ fontSize: 14, opacity: 0.7 }} />
                                    <MDTypography variant="caption" color="text.secondary">
                                      Terbaru: {group.latestWaktuFormatted}
                                </MDTypography>
                              </MDBox>
                                  <MDBox display="flex" alignItems="center" gap={0.5}>
                                    <PlaceIcon sx={{ fontSize: 14, opacity: 0.7 }} />
                                    <MDTypography variant="caption" color="text.secondary" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
                                      Tempat: {group.latestTempat}
                                  </MDTypography>
                                </MDBox>
                                  {group.dinasPesertaList?.length > 0 && (
                                    <MDBox display="flex" alignItems="flex-start" gap={0.5}>
                                      <BusinessIcon sx={{ fontSize: 14, opacity: 0.7, mt: 0.2 }} />
                                      <MDTypography variant="caption" color="text.secondary">
                                        Dinas: {group.dinasPesertaList.slice(0, 2).join(", ")}
                                        {group.dinasPesertaList.length > 2 && ` +${group.dinasPesertaList.length - 2}`}
                                  </MDTypography>
                                </MDBox>
                              )}
                                  <MDBox display="flex" alignItems="center" gap={0.5}>
                                    <PeopleIcon sx={{ fontSize: 14, opacity: 0.7 }} />
                                    <MDTypography variant="caption" color="text.secondary">
                                      Total peserta: {group.totalPeserta ?? 0}
                                        </MDTypography>
                                        </MDBox>
                                          </MDBox>
                                        <MDBox
                                          display="flex"
                        alignItems="center"
                                  gap={1}
                                  p={1}
                                sx={{
                                    borderRadius: 1,
                                    bgcolor: "grey.50",
                                    border: "1px solid",
                                    borderColor: "divider",
                                  }}
                                >
                                  <Tooltip title="Klik untuk tampilkan QR Code diperbesar">
                            <MDBox
                                      onClick={() => {
                                        setQrDialogEvent(group);
                                        setOpenQrDialog(true);
                                      }}
                                    sx={{
                                        flexShrink: 0,
                                        p: 0.5,
                                        bgcolor: "white",
                                        borderRadius: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        transition: "box-shadow 0.2s",
                                        "&:hover": { boxShadow: 1 },
                                      }}
                                    >
                                      <QRCodeSVG value={`${typeof window !== "undefined" ? window.location.origin : ""}/#/publik/events/daftar-hadir/${group.id_events}`} size={44} level="M" />
                                  </MDBox>
                                  </Tooltip>
                                  <MDBox flex={1} minWidth={0} display="flex" flexDirection="column" gap={0.25}>
                                    <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                                      Daftar Hadir
                                  </MDTypography>
                                    <MDBox display="flex" alignItems="center" gap={0.5}>
                                      <LinkIcon sx={{ fontSize: 14, color: "text.secondary", opacity: 0.8 }} />
                                <MDTypography
                                  variant="caption"
                                        color="primary"
                                  sx={{
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          cursor: "pointer",
                                          "&:hover": { textDecoration: "underline" },
                                        }}
                                        onClick={() => window.open(`${typeof window !== "undefined" ? window.location.origin : ""}/#/publik/events/daftar-hadir/${group.id_events}`, "_blank")}
                                      >
                                        Buka link
                                  </MDTypography>
                                </MDBox>
                            </MDBox>
                  </MDBox>
                                <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={1} pt={1} sx={{ borderTop: 1, borderColor: "divider" }}>
                                  <MDBox display="flex" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
                                    <MDBox position="relative" sx={{ width: 28, height: 28, flexShrink: 0 }}>
          <MDBox
            display="flex"
            alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                          position: "absolute",
                                          inset: 0,
                                          width: 28,
                                          height: 28,
                                          borderRadius: "50%",
                                          backgroundColor: "grey.200",
                                          color: "grey.500",
                                        }}
                                      >
                                        <PersonIcon sx={{ fontSize: 18 }} />
                </MDBox>
                                      {group.uid_RSS_pembuat && (
                                        <MDBox
                                          component="img"
                                          src={`https://smartdh.rotendaokab.go.id/uploads/${group.uid_RSS_pembuat}.jpg`}
                                          alt=""
                          sx={{
                              position: "absolute",
                                            inset: 0,
                                            width: 28,
                                            height: 28,
                                            borderRadius: "50%",
                              objectFit: "cover",
                            }}
                                          onError={(e) => { e.target.style.display = "none"; }}
                                        />
              )}
            </MDBox>
                                    <MDTypography variant="caption" color="text.secondary">
                                      {group.nama_pembuat || "-"}
                </MDTypography>
              </MDBox>
                                  <Tooltip title="Lihat Detail Event">
                                    <IconButton
              color="primary"
                                      size="small"
                                      onClick={() =>
                                        navigate(`/events/event-saya/detail/${group.id_events}`)
                                      }
                                      sx={{ bgcolor: "action.hover", flexShrink: 0 }}
                                    >
                                      <VisibilityIcon />
            </IconButton>
                                  </Tooltip>
          </MDBox>
                              </MDBox>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </MDBox>
                </Card>
              </Grid>
            </Grid>
          </MDBox>

      {/* Dialog QR Code Daftar Hadir */}
      <Dialog
        open={openQrDialog}
        onClose={() => {
          setOpenQrDialog(false);
          setQrDialogEvent(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6">Daftar Hadir – QR Code</MDTypography>
            <IconButton onClick={() => { setOpenQrDialog(false); setQrDialogEvent(null); }}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          {qrDialogEvent && (
            <MDBox pt={1} pb={3} px={3}>
              <MDBox mb={3}>
                <MDTypography variant="body2" color="text" sx={{ mb: 1, fontWeight: 500 }}>
                  {qrDialogEvent.kegiatan}
              </MDTypography>
                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                  <Chip label={getStatusEventLabel(qrDialogEvent.status_event)} color={getStatusEventColor(qrDialogEvent.status_event)} size="small" variant="filled" />
                  {qrDialogEvent.scope && (
                    <Chip label={getScopeLabel(qrDialogEvent.scope)} color={getScopeColor(qrDialogEvent.scope)} size="small" variant="outlined" />
                  )}
                  <MDTypography variant="caption" color="text.secondary">
                    {qrDialogEvent.jumlah_detail_events} sesi
                </MDTypography>
              </MDBox>
                {qrDialogEvent.latestTempat && (
                  <MDBox display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                    <PlaceIcon sx={{ fontSize: 14, opacity: 0.7 }} />
                    <MDTypography variant="caption" color="text.secondary">
                      {qrDialogEvent.latestTempat}
                            </MDTypography>
              </MDBox>
            )}
          </MDBox>
              <MDBox display="flex" flexDirection="column" alignItems="center" gap={2} p={2} sx={{ borderRadius: 2, bgcolor: "grey.50", border: "1px solid", borderColor: "divider" }}>
                <MDTypography variant="caption" color="text.secondary">
                  Scan QR Code berikut untuk mengisi daftar hadir
                </MDTypography>
                <MDBox sx={{ p: 2, bgcolor: "white", borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: 1 }}>
                  <QRCodeSVG value={`${typeof window !== "undefined" ? window.location.origin : ""}/#/publik/events/daftar-hadir/${qrDialogEvent.id_events}`} size={220} level="M" />
              </MDBox>
                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <LinkIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <MDTypography variant="caption" color="primary" sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }} onClick={() => window.open(`${typeof window !== "undefined" ? window.location.origin : ""}/#/publik/events/daftar-hadir/${qrDialogEvent.id_events}`, "_blank")}>
                    Buka link daftar hadir
                </MDTypography>
              </MDBox>
            </MDBox>
              </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenQrDialog(false); setQrDialogEvent(null); }} variant="contained" color="primary">
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default EventSaya;
